<template>
  <div class="auth-page">
    <UcariHeader />
    <main class="system-page">
    <form @submit.prevent="resetPassword" class="system-card system-card--form">
      <span class="system-card__icon" uk-icon="icon: lock; ratio: 1.5"></span>
      <span class="eyebrow">Secure your account</span>
      <h1>Reset Password</h1>
      <p>Choose a strong new password for your UCARI partner account.</p>
      <div v-if="message" class="uk-alert-success">{{ message }}</div>
      <div v-if="error" class="uk-alert-danger">{{ error }}</div>
      <span v-if="resetLink">
        <a :href="resetLink">Request new password reset link</a>
      </span>      
      <div class="uk-margin">
        <label for="newPassword">New Password</label>
        <div class="uk-inline">
          <input
            :type="showPassword ? 'text' : 'password'"
            class="uk-input"
            v-model="newPassword"
            @input="handlePasswordInput"
          />
          <button type="button" class="password-toggle" @click="togglePasswordVisibility">{{ showPassword ? 'Hide' : 'Show' }}</button>
        </div>
        <div class="password-requirements">
          <div><strong>Password must contain:</strong></div>
          <ul>
            <li :class="{ 'valid': passwordHasLowercase }">One lowercase letter</li>
            <li :class="{ 'valid': passwordHasUppercase }">One uppercase letter</li>
            <li :class="{ 'valid': passwordHasNumber }">One number</li>
            <li :class="{ 'valid': passwordHasSpecialChar }">One of these special character: ! @ # $ % ^ & * ( ) - + { } | ?</li>
            <li :class="{ 'valid': passwordHasLength }">At least 8 characters</li>
          </ul>
        </div>
      </div>
      <div class="uk-margin">
        <label for="confirmPassword">Confirm New Password</label>
        <div class="uk-inline">
          <input
            :type="showConfirmPassword ? 'text' : 'password'"
            class="uk-input"
            v-model="confirmNewPassword"
            @input="handlePasswordInput"
          />
          <button type="button" class="password-toggle" @click="toggleConfirmPasswordVisibility">{{ showConfirmPassword ? 'Hide' : 'Show' }}</button>
        </div>
        <div class="error" v-if="passwordMismatch">{{ passwordMismatch }}</div>
      </div>
      <button type="submit" class="uk-button uk-button-primary uk-width-1-1" :disabled="isFormInvalid">Update password</button>
    </form>
    </main>
    <RetailerFooter />
  </div>
</template>

<script>
import axios from 'axios';
import UcariHeader from './components/UcariHeader.vue'; 
import RetailerFooter from './components/RetailerFooter.vue'; 
import { validatePassword, toggleVisibility } from '@/utils/common';

export default {
  created() {
    document.title = 'Partner Portal Reset Password | UCARI';
  },  
  components: {
    UcariHeader,  
    RetailerFooter
  },
  data() {
    return {
      newPassword: '',
      confirmNewPassword: '',
      message: '',
      error: '',
      resetLink: '',
      showPassword: false,
      showConfirmPassword: false,
      isPasswordInvalid: false,
      passwordMismatch: ''
    };
  },
  computed: {
    isFormInvalid() {
      return this.isPasswordInvalid || this.passwordMismatch || !this.newPassword || !this.confirmNewPassword;
    },
    passwordHasLowercase() {
      return /[a-z]/.test(this.newPassword);
    },
    passwordHasUppercase() {
      return /[A-Z]/.test(this.newPassword);
    },
    passwordHasNumber() {
      return /\d/.test(this.newPassword);
    },
    passwordHasSpecialChar() {
      return /[!@#$%^&*()\-+{}|:;<>?/\\]/.test(this.newPassword);
    },
    passwordHasLength() {
      return this.newPassword.length >= 8;
    }
  },
  methods: {
    handlePasswordInput() {
      const { isPasswordInvalid, passwordMismatch } = validatePassword(this.newPassword, this.confirmNewPassword);
      this.isPasswordInvalid = isPasswordInvalid;
      this.passwordMismatch = passwordMismatch;
    },
    togglePasswordVisibility() {
      this.showPassword = toggleVisibility(this.showPassword);
    },
    toggleConfirmPasswordVisibility() {
      this.showConfirmPassword = toggleVisibility(this.showConfirmPassword);
    },
    async resetPassword() {
      if (this.isFormInvalid) {
        this.error = 'Please correct the errors in the form';
        return;
      }

      try {
        const response = await axios.post(`${process.env.VUE_APP_API_URL}/api/reset-password`, {
          token: this.$route.query.token,
          newPassword: this.newPassword
        });
        this.message = response.data.message;
        this.error = '';
        this.$router.push('/reset-password-successful');        
      } catch (error) {
        if (error.response?.data?.error === 'Token has expired') {
          this.error = 'Your token has expired.';
          this.resetLink = 'https://partner.ucari.com/request-password-reset';
        } else {
          this.error = error.response?.data?.error || 'An error occurred';
        }
        this.message = '';
      }
    }
  }
};
</script>
